import React from 'react';
import { TrendingUp, Plus, Banknote, Clock, Target } from 'lucide-react';
import { premiumTypeDescriptions } from '../utils/loadDescriptions';
import { SurchargeMethodSelector as SharedSurchargeMethodSelector, type SurchargeMethod } from '../../shared/surcharge/SurchargeMethodSelector';

type OvernightMethodValue = 'offset_overtime' | 'guaranteed_rate' | 'hourly_premium' | 'flat_rate' | 'credit_extra_time';

interface OvernightPremiumMethodSelectorProps {
  premiumType: OvernightMethodValue | null;
  onPremiumTypeChange: (value: OvernightMethodValue) => void;
  hasThresholdMultipliers: boolean;
  customizedFields?: Record<string, boolean>;
  context?: 'user' | 'client';
  markdownVariables: (method: string) => Record<string, unknown>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const overnightPremiumMethods: SurchargeMethod[] = [
  {
    value: 'offset_overtime',
    label: 'Offset Overtime',
    description: 'Bumps your pay to the next rate on the multiplier ladder during the overnight window.',
    icon: TrendingUp
  },
  {
    value: 'guaranteed_rate',
    label: 'Guaranteed Rate',
    description: 'Guarantees a minimum rate multiplier for hours worked during the overnight window.',
    icon: Target,
    buttonLabel: 'Guaranteed Rate',
    technicalName: 'Guaranteed Rate'
  },
  {
    value: 'hourly_premium',
    label: 'Hourly Premium',
    description: 'Adds a premium to the hourly rate during the overnight window. Can be a fixed amount or a percentage of the current hourly rate',
    icon: Plus
  },
  {
    value: 'flat_rate',
    label: 'Flat Rate Premium',
    description: 'Adds a single flat premium to the shift. This premium is independent of the shift duration, the base pay rate, or any overtime multipliers',
    icon: Banknote,
    buttonLabel: 'Flat Rate',
    technicalName: 'Flat Rate Premium'
  },
  {
    value: 'credit_extra_time',
    label: 'Credit Extra Time',
    description: 'Adds a premium for each hour in the window, equivalent to extra minutes\' pay at the current rate.',
    icon: Clock,
    buttonLabel: 'Credit Time',
    technicalName: 'Credit Extra Time'
  }
];

export const OvernightPremiumMethodSelector: React.FC<OvernightPremiumMethodSelectorProps> = ({
  premiumType,
  onPremiumTypeChange,
  hasThresholdMultipliers,
  customizedFields = {},
  context = 'user',
  markdownVariables
}) => {
  const isMethodDisabled = (method: SurchargeMethod): boolean => {
    if (method.value === 'offset_overtime') return !hasThresholdMultipliers;
    if (method.value === 'guaranteed_rate') return !hasThresholdMultipliers;
    return false;
  };

  const getDisabledTooltip = (): string => {
    return "Add at least one rate multiplier with a per-shift or per-week threshold to enable this mode.";
  };

  return (
    <SharedSurchargeMethodSelector
      id="overnight_premium_type"
      title="Overnight Premium Method"
      methods={overnightPremiumMethods}
      selectedValue={premiumType || ''}
      onValueChange={(value) => onPremiumTypeChange(value as NonNullable<OvernightPremiumMethodSelectorProps['premiumType']>)}
      methodDescriptions={premiumTypeDescriptions}
      isMethodDisabled={isMethodDisabled}
      getDisabledTooltip={getDisabledTooltip}
      markdownVariables={markdownVariables}
      columns={5}
      isCustomized={customizedFields.overnight_premium_type}
      context={context}
    />
  );
};
